/**
 * Step 6.7 -- choosing a character, entirely from the pack.
 *
 * Everything offered here comes out of `pack.manifest.json`: the slots, the variants a
 * gender can wear, the colourways, the tint regions and their suggested palettes, and
 * the stances the pack shipped clips for. Nothing is listed that the pack did not
 * enable, and nothing enabled is hidden — which is the payoff for the curation work in
 * M4 and M5.
 *
 * **Stance does not filter appearance.** The stance buttons change which animation
 * bundle loads and which weapon is attached; they touch no slot. A plate-armoured
 * archer is legitimate and this screen will let you build one.
 */
import type { Gender, StanceClass } from '@dungeon-builder/shared';

import type { PackManifest } from './localAvatar.ts';

export interface SelectionResult {
  gender: Gender;
  stance: StanceClass;
  variantIds: string[];
  colourway: string;
  tints: Record<string, string>;
}

export interface SelectScreenOptions {
  manifest: PackManifest;
  /** Fires on every edit, so the preview can follow. */
  onChange(selection: SelectionResult): void;
  onConfirm(selection: SelectionResult): void;
}

export class SelectScreen {
  private readonly root: HTMLElement;
  private selection: SelectionResult;

  constructor(
    container: HTMLElement,
    private readonly options: SelectScreenOptions,
  ) {
    const { manifest } = options;
    this.selection = {
      gender: 'male',
      stance: this.stances()[0] ?? 'swordsman',
      variantIds: this.defaultsFor('male'),
      colourway: manifest.colourways[0]?.id ?? '01',
      tints: {},
    };

    this.root = document.createElement('div');
    this.root.className = 'select-screen';
    container.append(this.root);
    this.render();
    this.options.onChange(this.selection);
  }

  /** Only the stances this pack actually shipped combat clips for. */
  private stances(): StanceClass[] {
    return this.options.manifest.bundles
      .flatMap((bundle) => (bundle.stance ? [bundle.stance] : []))
      .filter((stance, index, all) => all.indexOf(stance) === index);
  }

  private defaultsFor(gender: Gender): string[] {
    return this.options.manifest.slots.flatMap((slot) => {
      const wearable = slot.variants
        .filter((variant) => variant.scope === 'shared' || variant.scope === gender)
        .sort((a, b) => Number(b.default) - Number(a.default) || a.index - b.index);
      return wearable[0] ? [wearable[0].id] : [];
    });
  }

  private change(patch: Partial<SelectionResult>): void {
    this.selection = { ...this.selection, ...patch };
    this.render();
    this.options.onChange(this.selection);
  }

  private render(): void {
    const { manifest } = this.options;
    this.root.textContent = '';

    const heading = document.createElement('h2');
    heading.textContent = manifest.name;
    this.root.append(heading);

    // --- gender ---
    this.root.append(
      this.segmented('Body', ['male', 'female'], this.selection.gender, (value) =>
        // Changing gender re-picks every slot: the meshes are not interchangeable.
        this.change({ gender: value as Gender, variantIds: this.defaultsFor(value as Gender) }),
      ),
    );

    // --- stance ---
    const stances = this.stances();
    if (stances.length > 0) {
      this.root.append(
        this.segmented('Stance', stances, this.selection.stance, (value) =>
          this.change({ stance: value as StanceClass }),
        ),
      );
      const note = document.createElement('p');
      note.className = 'select-note';
      note.textContent = 'Stance changes how you fight, never how you look.';
      this.root.append(note);
    }

    // --- slots ---
    for (const slot of manifest.slots) {
      const wearable = slot.variants.filter(
        (variant) => variant.scope === 'shared' || variant.scope === this.selection.gender,
      );
      if (wearable.length <= 1) continue;

      const row = document.createElement('div');
      row.className = 'select-slot';
      const label = document.createElement('span');
      label.textContent = slot.slot;
      row.append(label);

      for (const variant of wearable.sort((a, b) => a.index - b.index)) {
        const button = document.createElement('button');
        button.type = 'button';
        button.textContent = String(variant.index).padStart(2, '0');
        button.setAttribute(
          'aria-pressed',
          String(this.selection.variantIds.includes(variant.id)),
        );
        button.addEventListener('click', () => {
          const ids = this.selection.variantIds.filter(
            (id) => !wearable.some((entry) => entry.id === id),
          );
          this.change({ variantIds: [...ids, variant.id] });
        });
        row.append(button);
      }
      this.root.append(row);
    }

    // --- colourway ---
    if (manifest.colourways.length > 1) {
      this.root.append(
        this.segmented(
          'Colourway',
          manifest.colourways.map((entry) => entry.id),
          this.selection.colourway,
          (value) => this.change({ colourway: value }),
        ),
      );
    }

    // --- tints ---
    for (const region of manifest.tintRegions) {
      const row = document.createElement('div');
      row.className = 'select-slot';
      const label = document.createElement('span');
      label.textContent = region.id;
      row.append(label);

      const picker = document.createElement('input');
      picker.type = 'color';
      picker.value = this.selection.tints[region.id] ?? '#ffffff';
      picker.addEventListener('input', () =>
        this.change({ tints: { ...this.selection.tints, [region.id]: picker.value } }),
      );
      row.append(picker);

      // Swatches the builder suggested, if any.
      for (const swatch of region.palette ?? []) {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'select-swatch';
        button.style.background = swatch;
        button.title = swatch;
        button.addEventListener('click', () =>
          this.change({ tints: { ...this.selection.tints, [region.id]: swatch } }),
        );
        row.append(button);
      }
      this.root.append(row);
    }

    const confirm = document.createElement('button');
    confirm.type = 'button';
    confirm.className = 'select-confirm';
    confirm.textContent = 'Enter the dungeon';
    confirm.addEventListener('click', () => this.options.onConfirm(this.selection));
    this.root.append(confirm);
  }

  private segmented(
    label: string,
    values: string[],
    active: string,
    onPick: (value: string) => void,
  ): HTMLElement {
    const row = document.createElement('div');
    row.className = 'select-slot';
    const caption = document.createElement('span');
    caption.textContent = label;
    row.append(caption);
    for (const value of values) {
      const button = document.createElement('button');
      button.type = 'button';
      button.textContent = value;
      button.setAttribute('aria-pressed', String(value === active));
      button.addEventListener('click', () => onPick(value));
      row.append(button);
    }
    return row;
  }

  get current(): SelectionResult {
    return this.selection;
  }

  dispose(): void {
    this.root.remove();
  }
}
